import React, { useState, useEffect, useTransition } from 'react';
import { useParams, useNavigate } from 'react-router';
import ProjectForm from './Form/ProjectForm';
import EmployeeProject from './EmployeeProject/EmployeeProject';
import { EmployeeAllocationTimeline } from './EmployeeProject/EmployeeAllocationTimeline';
import JobProject from './Job/JobProject';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { Client, ClientContact, CreateProjectCommand, UpdateProjectCommand } from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ButtonWithOptions } from "components/common/ButtonWithOptions";
import { ROLES } from "common/permissions";
import { PermissionsGate } from "components/authorization/PermissionsGate";
import { HoursByEmployeeReportComponent } from "components/Reports/HoursByEmployeeReportComponent";
import intl from 'components/i18n/ReactIntlWrapper';
import Spinner from '../Spinner';
import { Save, Edit } from "@mui/icons-material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Typography, Button, Grid, Box, Card, MenuItem } from '@mui/material';

export type ProjectDetailInput = {
  name?: string | null;
  idClient?: number | null;
  client?: {
    name: string | null;
    tradeName: string | null;
    clientContact: ClientContact | any;
  };
  jiraUser?: string | null;
  jiraToken?: string | null;
  jiraProjectKey?: string | null;
  jiraUrl?: string | null;
  budget?: number | null;
  currentPage?: number;
};

const ProjectDetail = () => {
  const [startDate, setStartDate] = useState(new Date(Date.parse('2020-01-01')));
  const [endDate, setEndDate] = useState(new Date(Date.now()));
  const [chartRefreshKey, setChartRefreshKey] = useState(0);


    const [input, setInput] = useState<ProjectDetailInput | null>({
        name: '',
        idClient: null,
        client: {
            name: '',
            tradeName: '',
            clientContact: { client: { name: '' } },
        },
        jiraUser: '',
        jiraToken: '',
        jiraProjectKey: '',
        jiraUrl: '',
        budget: 0,
        currentPage: 1
    });

  const api: Api = useApi();

  const { idProject } = useParams();
  const idProjectNumber = parseInt(idProject);
  const [filterData, setFilterData] = useState({
    from: startDate,
    projects: idProject,
    clients: '',
    employees: '',
    jobTypes: '',
    to: endDate,
  });
  const [clients, setClients] = useState<Client[]>(null);

  const [errors, setErrors] = useState({});

  const [disabled, setDisabled] = useState(false);

  const [disabledNew, setDisabledNew] = useState(false);

  const navigate = useNavigate();

  const toast = useToastMessageQueue();

  const [loading, setLoading] = useState<boolean>(false);

  const [isLoading, startFetchTransition] = useTransition();

  const fetchProjectDetail = async (idProjectNumber: number) => {
    startFetchTransition(async () => {
      await api.projectApi
        .apiVversionProjectIdGet(idProjectNumber, '1', {})
        .then((response) => {
          if (response.data) {
            setInput({
              name: response.data?.data?.project?.name,
              idClient: response.data?.data?.project?.idClient,
              client: {
                name: response.data?.data?.project?.client?.name,
                tradeName: response.data?.data?.project?.client?.tradeName,
                clientContact: {
                  client: { name: response.data?.data?.project?.client?.clientContact },
                },
              },
              jiraUser: response.data?.data?.project?.jiraUser,
              jiraToken: response.data?.data?.project?.jiraToken,
              jiraProjectKey: response.data?.data?.project?.jiraProjectKey,
              jiraUrl: response.data?.data?.project?.jiraUrl,
              budget: response.data?.data?.project?.budget,
            });
          }
        })
        .catch((error) => {
          if (error.response) {
            console.log('Data :', error.response.data);
            console.log('Status :' + error.response.status);
          } else if (error.request) {
            console.log(error.request);
          } else {
            console.log('Error', error.message);
          }
          toast.error({
            header: intl.get('projectDetail.toast.error.fetchProjectDetail'),
            message: error.message,
          });
        });
    });
  };

  const fetchClients = async () => {
    const response = await api.clientsApi
      .apiVversionClientAllGet('1', 1, 100, {})
      .then((response) => {
        if (response.data.data) {
          setClients(response.data.data.queryResult);
        }
      })
      .catch((error) => {
        if (error.response) {
          console.log('Data :', error.response.data);
          console.log('Status :' + error.response.status);
        } else if (error.request) {
          console.log(error.request);
        } else {
          console.log('Error', error.message);
        }
        toast.error({
          header: intl.get('projectDetail.toast.error.fetchClients'),
          message: error.message,
        });
      });
  };

  useEffect(() => {
    fetchClients();
    if (idProjectNumber) {
      fetchProjectDetail(idProjectNumber);
      setDisabled(true);
    }
    setDisabledNew(true);
  }, [idProjectNumber]);

  const handleInputChange = function (e) {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const handleClientChange = (e) => {
    const inputVal = {
      ...input,
      idClient: e.id,
      client: {
        name: e.name,
        tradeName: e.tradeName,
        clientContact: { client: { name: e.clientContact } },
      },
    };
    setInput({ ...inputVal });
    setErrors(
      validate({
        ...input,
        idClient: e.id,
      })
    );
  };

  const validate = function (input: ProjectDetailInput) {
    setDisabledNew(false);
    let errors: any = {};
    if (!input.name) {
      errors.name = intl.get('validate.errors.isRequired');
    }
    if (!input.idClient) {
      errors.idClient = intl.get('validate.errors.isRequired');
    }
    if (
      (input.jiraToken || input.jiraProjectKey || input.jiraUrl) &&
      !input.jiraUser
    ) {
      errors.jiraUser = intl.get('validate.errors.jiraFields');
    }
    if ((input.jiraUser || input.jiraProjectKey || input.jiraUrl) && !input.jiraToken) {
      errors.jiraToken = intl.get('validate.errors.jiraFields');
    }
    if (
      (input.jiraToken || input.jiraUser || input.jiraUrl) &&
      !input.jiraProjectKey
    ) {
      errors.jiraProjectKey = intl.get('validate.errors.jiraFields');
    }
    if (
      (input.jiraToken || input.jiraProjectKey || input.jiraUser) &&
      !input.jiraUrl
    ) {
      errors.jiraUrl = intl.get('validate.errors.jiraFields');
    } else if (
      input.jiraUrl &&
      !/^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(
        input.jiraUrl
      )
    ) {
      errors.jiraUrl = intl.get('validate.errors.isRequired');
    }
    if (!input.budget && input.budget !== 0) {
      errors.budget = intl.get('validate.errors.budgetMoreEqualZero');
    } else if (input.budget < 0) {
      errors.budget = intl.get('validate.errors.positive');
    }
    return errors;
  };

  const handleSubmit = async (saveAndEdit: boolean = false) => {
    setLoading(true);
    const errors = validate(input);
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      if (idProjectNumber) {
        let cmd: UpdateProjectCommand = {
          id: idProjectNumber,
          name: input.name,
          idClient: input.idClient,
          jiraUser: input.jiraUser,
          jiraToken: input.jiraToken,
          jiraProjectKey: input.jiraProjectKey,
          jiraUrl: input.jiraUrl,
          budget: input.budget,
        };
        const response = await api.projectApi
          .apiVversionProjectPut('1', cmd, {})
          .then((response) => {
            if (saveAndEdit) setDisabled(true);
            else navigate('/projects');
            toast.success({ message: intl.get('projectDetail.toast.success.handleSubmit') });
            setLoading(false);
          })
          .catch((error) => {
            if (error.response) {
              console.log('Data :', error.response.data);
              console.log('Status :' + error.response.status);
            } else if (error.request) {
              console.log(error.request);
            } else {
              console.log('Error', error.message);
            }
            toast.error({
              header: intl.get('projectDetail.toast.error.handleSubmit'),
              message: error.message,
            });
            setLoading(false);
          });
      } else {
        let cmd: CreateProjectCommand = {
          name: input.name,
          idClient: input.idClient,
          jiraUser: input.jiraUser,
          jiraToken: input.jiraToken,
          jiraProjectKey: input.jiraProjectKey,
          jiraUrl: input.jiraUrl,
          budget: input.budget,
        };
        const response = await api.projectApi
          .apiVversionProjectPost('1', cmd, {})
          .then((response) => {
            toast.success({ message: intl.get('projectDetail.toast.success.handleSubmit.add') });
            setLoading(false);
            if (saveAndEdit) navigate(`/project/detail/${response.data.data.id}`);
            else navigate('/projects');
          })
          .catch((error) => {
            if (error.response) {
              console.log('Data :', error.response.data);
              console.log('Status :' + error.response.status);
            } else if (error.request) {
              console.log(error.request);
            } else {
              console.log('Error', error.message);
            }
            toast.error({
              header: intl.get('projectDetail.toast.error.handleSubmit.add'),
              message: error.message,
            });
            setLoading(false);
          });
      }
    } else {
      setLoading(false);
    }
  };

  const isJiraReady = () => {
    return (
      input.jiraProjectKey != '' &&
      input.jiraToken != '' &&
      input.jiraUrl != '' &&
      input.jiraUser != ''
    );
  };

  const handleReturn = () => {
    navigate(-1);
  };

  const handleEditMode = (e) => {
    e.preventDefault();
    if (!disabled) {
      setInput({ ...input, jiraToken: input.jiraToken == '**encrypted**' ? '' : input.jiraToken });
    } else if (input.jiraToken == '') {
      setInput({ ...input, jiraToken: '**encrypted**' });
    }
    setErrors({});
    if (idProjectNumber) {
      fetchProjectDetail(idProjectNumber);
    }
    setDisabled(!disabled);
  };

  const handleSaveClick = () => {
    handleSubmit(true);
  };

  const handleSaveCloseClick = () => {
    handleSubmit(false);
  };

  return (
    <Box sx={{ p: 3 }}>
        <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
          {idProject ? (
            <>{intl.get('projectDetail.header')}</>
          ) : (
            <>{intl.get('newProject.header')}</>
          )}
        </Typography>
          {isLoading ? (
            <Box display={'flex'} justifyContent={'center'} gap={1} my={2}>
              <Spinner small={true} />
            </Box>
          ) : (
            <ProjectForm
              errors={errors}
              input={input}
              handleInputChange={handleInputChange}
              handleClientChange={handleClientChange}
              clients={clients}
              selectedClient={input.idClient}
              disabled={disabled}
              idProject={idProject}
              validate={validate}
              setErrors={setErrors}
            />
          )}
          <Grid container spacing={2} mt={2} justifyContent="flex-end">
            {!idProject && !disabled ? (
              <>
                {loading === true ? (
                  <Box display={'flex'} justifyContent={'center'} gap={1} my={2}>
                    <Spinner small={true} />
                  </Box>
                ) : (
                  <>
                    <Button id="return" onClick={handleReturn}>
                      <KeyboardBackspaceIcon />
                    </Button>
                    <PermissionsGate
                      identifier="ui.projects.detail.info-form"
                      editRoles={[ROLES.projectReader]}
                      viewRoles={[ROLES.projectReader]}
                    >
                      <ButtonWithOptions
                        startIcon={<Save />}
                        onClick={handleSaveClick}
                        menu={
                          <>
                            <MenuItem onClick={handleSaveCloseClick}>
                              {intl.get('save&close.button')}
                            </MenuItem>
                          </>
                        }
                        disabled={disabledNew}
                      >
                        {intl.get('save.button')}
                      </ButtonWithOptions>
                    </PermissionsGate>
                  </>
                )}
              </>
            ) : null}
            {idProject && disabled ? (
              <>
                <Button id="return" onClick={handleReturn}>
                  <KeyboardBackspaceIcon />
                </Button>
                <Button startIcon={<Edit />} id="edit" onClick={handleEditMode}>
                  {intl.get('edit.button')}
                </Button>
              </>
            ) : null}
            {idProject && !disabled ? (
              <>
                {loading === true ? (
                  <Spinner small={true} />
                ) : (
                  <>
                    <Button id="edit" onClick={handleEditMode}>
                      {intl.get('cancel.button')}
                    </Button>
                    <PermissionsGate
                      identifier="ui.projects.detail.assignment-form"
                      editRoles={[ROLES.projectReader]}
                      viewRoles={[ROLES.projectReader]}
                    >
                      <ButtonWithOptions
                        startIcon={<Save />}
                        onClick={handleSaveClick}
                        menu={
                          <>
                            <MenuItem onClick={handleSaveCloseClick}>
                              {intl.get('save&close.button')}
                            </MenuItem>
                          </>
                        }
                      >
                        {intl.get('save.button')}
                      </ButtonWithOptions>
                    </PermissionsGate>
                  </>
                )}
              </>
            ) : null}
          </Grid>
        {idProject && (
          <>
            <Box mt={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <EmployeeProject
                  idProject={idProjectNumber}
                  disabled={disabled}
                  errors={errors}
                  setErrors={setErrors}
                  onDataChange={() => setChartRefreshKey(prev => prev + 1)}
                />
              </Card>
            </Box>
            <EmployeeAllocationTimeline
              idProject={idProjectNumber}
              disabled={disabled}
              refreshKey={chartRefreshKey}
            />
            <Box mt={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <JobProject
                  idProject={idProjectNumber}
                  isJiraReady={isJiraReady()}
                  projectName={input.name}
                  clientName={input.client.name}
                  disabled={disabled}
                  errors={errors}
                  setErrors={setErrors}
                />
              </Card>
            </Box>
            {/* TODO WHOLE COMPONENT: <ProjectBudgetStatusReport projectId={idProject}  startDate={startDate} budget={input.budget} errors={errors} setErrors={setErrors}/>  */}
            <Box mt={4}>
              <Card variant="outlined" sx={{ p: 2 }}>
                <HoursByEmployeeReportComponent
                  filterData={filterData}
                  errors={errors}
                  setErrors={setErrors}
                />
              </Card>
            </Box>
          </>
        )}
      </Box>
  );
};



export default ProjectDetail;
